import React, { useState, useEffect } from 'react';
import { IonIcon, IonButton, IonRadioGroup, IonRadio, IonLabel, IonItem } from '@ionic/react';
import { close } from 'ionicons/icons';
import Modal from './Modal';
import './LoreBotConsentModal.css';

interface LoreBotConsentModalProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** Callback fired when modal should close */
  onClose: () => void;
  /** Callback fired when user confirms, receives true if consent selected (keep on), false if decline selected (turn off) */
  onConfirm: (shouldKeepOn: boolean) => void;
  /** Additional CSS class name */
  className?: string;
}

function LoreBotConsentModal({
  isOpen,
  onClose,
  onConfirm,
  className = '',
}: LoreBotConsentModalProps) {
  const [selectedOption, setSelectedOption] = useState<string>('');

  // Reset to no selection when modal opens
  useEffect(() => {
    if (isOpen) {
      setSelectedOption('');
    }
  }, [isOpen]);

  const handleConfirm = () => {
    // If consent is selected, keep toggle on (true), otherwise turn it off (false)
    const shouldKeepOn = selectedOption === 'consent';
    onConfirm(shouldKeepOn);
    onClose();
  };

  const handleClose = () => {
    // When X is clicked, ensure toggle stays off
    onConfirm(false);
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      className={className}
    >
      <div className="lorebot-consent-modal-content">
        <IonButton onClick={handleClose} className="lorebot-consent-modal-close-button">
          <IonIcon icon={close} />
        </IonButton>

        <div className="lorebot-consent-modal-heading">
          LoreBot Consent
        </div>
        <div className="lorebot-consent-modal-text">
          Lore treats certain personal information with special sensitivity - that is information you share with LoreBot.
          You can control how LoreBot collects, uses, and stores this information.
          <strong>You can change your choice or opt-out at any time.</strong>
        </div>

        <div className="lorebot-consent-modal-text">
          By chatting with LoreBot, you consent to:
          <ul>
            <li>
              Lore's collection, use, and storage of personal information you 
              share in chats, including information about your health, as 
              described in our <a href="https://lore.co/privacypolicy">Privacy Notice</a>.
            </li>
            <li>
              Lore's use of your personal information to customize our service for 
              you and match your interests and preferences.
            </li>
          </ul>
        </div>

        <div className="lorebot-consent-modal-radio-group">
          <IonRadioGroup value={selectedOption} onIonChange={(e) => setSelectedOption(e.detail.value)}>
            <IonItem lines="none" className="lorebot-consent-modal-radio-item">
              <IonRadio slot="start" value="decline" />
              <IonLabel><strong>I do not want</strong> to chat with LoreBot</IonLabel>
            </IonItem>
            <IonItem lines="none" className="lorebot-consent-modal-radio-item">
              <IonRadio slot="start" value="consent" />
              <IonLabel><strong>I want</strong> to chat with LoreBot</IonLabel>
            </IonItem>
          </IonRadioGroup>
        </div>
        <div className="lorebot-consent-modal-button-wrapper">
          <IonButton
            className="lorebot-consent-modal-confirm-button"
            disabled={selectedOption === ''}
            onClick={handleConfirm}
          >
            Confirm
          </IonButton>
        </div>
      </div>
    </Modal>
  );
}

export default LoreBotConsentModal;

